import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useCustomerAuth } from '../../contexts/CustomerAuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Calendar, ChevronRight, Loader2, Trophy, Sparkles,
  Gift, AlertTriangle, Zap, TrendingUp, Clock, MapPin,
  Flame, BarChart3, Gamepad2
} from 'lucide-react';
import CustomerFooter from '../../components/CustomerFooter';
import NaturalLanguageBooking from '../../components/NaturalLanguageBooking';

import { BACKEND_URL, API } from '../../utils/apiConfig';

const PickleballIcon = () => (
  <svg viewBox="0 0 64 64" className="w-8 h-8" fill="none" xmlns="http://www.w3.org/2000/svg">
    <ellipse cx="24" cy="28" rx="14" ry="18" fill="white" stroke="white" strokeWidth="2"/>
    <rect x="20" y="44" width="8" height="14" rx="2" fill="#FCD34D" stroke="#F59E0B" strokeWidth="1"/>
    <circle cx="46" cy="32" r="12" fill="#84CC16" stroke="#65A30D" strokeWidth="2"/>
    <circle cx="42" cy="28" r="2" fill="#65A30D"/>
    <circle cx="50" cy="28" r="2" fill="#65A30D"/>
    <circle cx="42" cy="36" r="2" fill="#65A30D"/>
    <circle cx="50" cy="36" r="2" fill="#65A30D"/>
    <circle cx="46" cy="32" r="2" fill="#65A30D"/>
  </svg>
);

const GAME_CONFIG = {
  'Pickleball': { emoji: 'pickleball', gradient: 'from-emerald-400 via-teal-500 to-cyan-500', shadow: 'shadow-emerald-500/30', isCustomIcon: true, color: '#10b981' },
  'Badminton': { emoji: '\ud83c\udff8', gradient: 'from-blue-400 via-indigo-500 to-purple-500', shadow: 'shadow-indigo-500/30', color: '#6366f1' },
  'Football': { emoji: '\u26bd', gradient: 'from-orange-400 via-red-500 to-rose-500', shadow: 'shadow-red-500/30', color: '#ef4444' },
  'Football Half': { emoji: '\u26bd', gradient: 'from-amber-400 via-orange-500 to-red-500', shadow: 'shadow-orange-500/30', color: '#f97316' },
  'Football Full': { emoji: '\u26bd', gradient: 'from-rose-400 via-red-500 to-pink-500', shadow: 'shadow-rose-500/30', color: '#e11d48' },
  'Cricket': { emoji: '\ud83c\udfd0', gradient: 'from-yellow-400 via-amber-500 to-orange-500', shadow: 'shadow-amber-500/30', color: '#f59e0b' },
  'Tennis': { emoji: '\ud83c\udfbe', gradient: 'from-lime-400 via-green-500 to-emerald-500', shadow: 'shadow-green-500/30', color: '#22c55e' },
  'Basketball': { emoji: '\ud83c\udfc0', gradient: 'from-red-400 via-rose-500 to-pink-500', shadow: 'shadow-rose-500/30', color: '#f43f5e' },
  'default': { emoji: '\ud83c\udfaf', gradient: 'from-slate-400 via-slate-500 to-slate-600', shadow: 'shadow-slate-500/30', color: '#64748b' }
};

export default function CustomerHomePage() {
  const { customer, isProfileComplete } = useCustomerAuth();
  const navigate = useNavigate();
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [upcomingBookings, setUpcomingBookings] = useState([]);
  const [bookingsLoading, setBookingsLoading] = useState(true);
  const [announcements, setAnnouncements] = useState([]);
  const [currentAnnouncementIndex, setCurrentAnnouncementIndex] = useState(0);
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [socialProof, setSocialProof] = useState(null);

  const fetchAnnouncements = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/announcements/active`);
      setAnnouncements(response.data.announcements || []);
    } catch (error) {
      console.error('Failed to fetch announcements:', error);
    }
  }, []);

  useEffect(() => {
    if (!isProfileComplete) {
      navigate('/profile-setup');
      return;
    }
    fetchGames();
    fetchUpcomingBookings();
    fetchAnnouncements();
    fetchSuggestions();
    fetchStats();
    fetchSocialProof();
  }, [isProfileComplete, navigate, fetchAnnouncements]);

  useEffect(() => {
    if (announcements.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentAnnouncementIndex((prev) => (prev + 1) % announcements.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [announcements.length]);

  const fetchGames = async () => {
    try {
      const response = await axios.get(`${API}/user/games`);
      setGames(response.data.games || []);
    } catch (error) {
      console.error('Failed to fetch games:', error);
      toast.error('Failed to load games');
    } finally {
      setLoading(false);
    }
  };

  const fetchUpcomingBookings = async () => {
    try {
      const token = localStorage.getItem('customer_token');
      const response = await axios.get(`${API}/user/bookings?booking_status=upcoming`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUpcomingBookings(response.data.bookings?.slice(0, 2) || []);
    } catch (error) {
      console.error('Failed to fetch bookings:', error);
    } finally {
      setBookingsLoading(false);
    }
  };

  const fetchSuggestions = async () => {
    try {
      const token = localStorage.getItem('customer_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/user/smart-suggestions`, { headers });
      setSuggestions(response.data.suggestions || []);
    } catch (error) {
      console.error('Failed to fetch suggestions:', error);
    } finally {
      setSuggestionsLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('customer_token');
      const response = await axios.get(`${API}/user/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setStatsLoading(false);
    }
  };

  const fetchSocialProof = async () => {
    try {
      const response = await axios.get(`${API}/user/social-proof`);
      setSocialProof(response.data);
    } catch (error) {
      console.error('Failed to fetch social proof:', error);
    }
  };

  const handleGameSelect = (game) => {
    navigate(`/venues?game=${encodeURIComponent(game)}`);
  };

  const handleSuggestionBook = (s) => {
    navigate(`/venues?game=${encodeURIComponent(s.game)}`);
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    if (dateStr === today.toISOString().split('T')[0]) return 'Today';
    if (dateStr === tomorrow.toISOString().split('T')[0]) return 'Tomorrow';
    return date.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });
  };

  const getGameConfig = (game) => GAME_CONFIG[game] || GAME_CONFIG['default'];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50 to-pink-50">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-violet-600 via-purple-600 to-fuchsia-600" />
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNiIgc3Ryb2tlPSJyZ2JhKDI1NSwyNTUsMjU1LDAuMSkiIHN0cm9rZS13aWR0aD0iMiIvPjwvZz48L3N2Zz4=')] opacity-30" />
        
        <div className="relative px-4 py-6">
          <div className="flex items-center gap-2 mb-1">
            <Sparkles className="w-5 h-5 text-yellow-300" />
            <span className="text-purple-200 text-sm">Welcome back</span>
          </div>
          <h1 className="text-2xl font-bold text-white mb-1">
            Hey, {customer?.name?.split(' ')[0] || 'Player'}!
          </h1>
          <p className="text-purple-200 text-sm">Ready to play today?</p>
          
          {/* Announcement Carousel */}
          {announcements.length > 0 && (
            <div className="mt-3 overflow-hidden" data-testid="announcement-carousel">
              <div className="flex items-center gap-2 bg-white/15 backdrop-blur-sm rounded-full px-3 py-1.5 border border-white/20">
                {announcements[currentAnnouncementIndex]?.type === 'offer' && (
                  <Gift className="w-4 h-4 text-emerald-300 flex-shrink-0" />
                )}
                {announcements[currentAnnouncementIndex]?.type === 'tournament' && (
                  <Trophy className="w-4 h-4 text-yellow-300 flex-shrink-0" />
                )}
                {announcements[currentAnnouncementIndex]?.type === 'alert' && (
                  <AlertTriangle className="w-4 h-4 text-amber-300 flex-shrink-0" />
                )}
                <span 
                  key={currentAnnouncementIndex}
                  className="text-xs sm:text-sm text-white font-medium truncate animate-fade-in"
                >
                  {announcements[currentAnnouncementIndex]?.text}
                </span>
                {announcements.length > 1 && (
                  <div className="flex gap-1 ml-auto flex-shrink-0">
                    {announcements.map((_, idx) => (
                      <div 
                        key={idx}
                        className={`w-1.5 h-1.5 rounded-full transition-all ${
                          idx === currentAnnouncementIndex ? 'bg-white w-3' : 'bg-white/40'
                        }`}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Social Proof - FOMO Pill */}
          {socialProof && (
            <div className="mt-2 overflow-hidden" data-testid="social-proof-pill">
              <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-full px-3 py-1.5 border border-white/15">
                <div className="flex items-center gap-1.5">
                  <Flame className="w-3.5 h-3.5 text-orange-300 flex-shrink-0" />
                  <span className="text-xs text-white font-medium">{socialProof.players_today} players playing today</span>
                </div>
                <div className="w-1 h-1 rounded-full bg-white/30 flex-shrink-0" />
                <div className="flex items-center gap-1.5">
                  <Gamepad2 className="w-3.5 h-3.5 text-cyan-300 flex-shrink-0" />
                  <span className="text-xs text-white font-medium">{socialProof.games_today} games happening now</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ===== Upcoming Bookings - VISIBLE REMINDER ===== */}
      {!bookingsLoading && upcomingBookings.length > 0 && (
        <div className="px-4 pt-4 pb-1" data-testid="upcoming-bookings-reminder">
          {upcomingBookings.map((booking, idx) => {
            const config = getGameConfig(booking.game);
            return (
              <Link
                key={idx}
                to="/my-bookings"
                className="block mb-2 bg-white rounded-xl border border-amber-200 shadow-sm hover:shadow-md hover:border-amber-300 transition-all overflow-hidden"
                data-testid={`upcoming-booking-${idx}`}
              >
                <div className={`h-1 bg-gradient-to-r ${config.gradient}`} />
                <div className="flex items-center gap-3 p-3">
                  <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${config.gradient} flex items-center justify-center text-xl shadow-sm ${config.shadow} flex-shrink-0`}>
                    {config.isCustomIcon ? <PickleballIcon /> : config.emoji}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 mb-0.5">
                      <Clock className="w-3.5 h-3.5 text-amber-500" />
                      <span className="text-xs font-semibold text-amber-600">Your Next Game</span>
                    </div>
                    <p className="font-bold text-slate-900 text-sm truncate">{booking.game}</p>
                    <p className="text-xs text-slate-500">
                      {formatDate(booking.date)} &bull; {booking.start_time} &bull; {booking.venue}
                    </p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-slate-300 flex-shrink-0" />
                </div>
              </Link>
            );
          })}
        </div>
      )}

      {/* ===== PLAY NOW - Smart Suggestions ===== */}
      {!suggestionsLoading && suggestions.length > 0 && (
        <div className="px-4 pt-5 pb-2" data-testid="smart-suggestions-section">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center shadow-md">
              <Flame className="w-4 h-4 text-white" />
            </div>
            <div>
              <h2 className="text-base font-bold text-slate-900">Recommended for You</h2>
              <p className="text-xs text-slate-500">Quick book an open slot</p>
            </div>
          </div>
          <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 no-scrollbar">
            {suggestions.map((s, idx) => {
              const config = getGameConfig(s.game);
              return (
                <button
                  key={idx}
                  onClick={() => handleSuggestionBook(s)}
                  className="flex-shrink-0 w-56 bg-white rounded-xl border border-slate-100 shadow-sm hover:shadow-md hover:border-slate-200 transition-all p-3 text-left group"
                  data-testid={`suggestion-card-${idx}`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${config.gradient} flex items-center justify-center text-lg shadow-sm`}>
                      {config.isCustomIcon ? <PickleballIcon /> : config.emoji}
                    </div>
                    <span className="text-sm font-semibold text-slate-800 truncate">{s.game}</span>
                  </div>
                  <div className="flex items-center gap-1 text-xs text-slate-500 mb-1">
                    <Clock className="w-3 h-3" />
                    <span>{s.start_time} &ndash; {s.end_time}</span>
                  </div>
                  <div className="flex items-center gap-1 text-xs text-slate-500 mb-2">
                    <MapPin className="w-3 h-3" />
                    <span className="truncate">{s.venue_location || s.venue_name}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-emerald-600">{s.price > 0 ? `\u20b9${s.price}` : 'Free'}</span>
                    <span className="text-xs font-semibold text-violet-600 group-hover:text-violet-700 flex items-center gap-0.5">
                      Book <ChevronRight className="w-3 h-3" />
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* ===== Player Stats Card ===== */}
      {!statsLoading && stats && stats.total_games > 0 && (
        <div className="px-4 pt-3 pb-1" data-testid="player-stats-section">
          <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-4">
            <div className="flex items-center gap-2 mb-3">
              <BarChart3 className="w-4 h-4 text-violet-500" />
              <span className="text-sm font-bold text-slate-800">Your Stats</span>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="text-center">
                <p className="text-2xl font-bold text-slate-900" data-testid="stat-total-games">{stats.total_games}</p>
                <p className="text-xs text-slate-500">Games Played</p>
              </div>
              <div className="text-center border-x border-slate-100">
                <p className="text-2xl font-bold text-violet-600" data-testid="stat-this-week">{stats.this_week}</p>
                <p className="text-xs text-slate-500">This Week</p>
              </div>
              <div className="text-center">
                <div className="flex items-center justify-center gap-1">
                  {stats.favorite_sport && (
                    <span className="text-lg">
                      {getGameConfig(stats.favorite_sport).isCustomIcon
                        ? <span className="inline-block w-6 h-6"><PickleballIcon /></span>
                        : getGameConfig(stats.favorite_sport).emoji}
                    </span>
                  )}
                </div>
                <p className="text-xs text-slate-500 mt-0.5">{stats.favorite_sport || '-'}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ===== Main Content - 3 Sections ===== */}
      <div className="px-4 py-4 space-y-0">

        {/* ── Section 1: Ask Maksio ── */}
        <div className="pb-4" data-testid="section-quick-book">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-sm">
              <Zap className="w-3.5 h-3.5 text-white" />
            </div>
            <div>
              <h2 className="text-base font-bold text-slate-900">Ask Maksio</h2>
              <p className="text-[10px] text-slate-400 -mt-0.5">Powered by MAKS AI Intelligence</p>
            </div>
          </div>
          <NaturalLanguageBooking />
        </div>

        {/* Separator */}
        <div className="flex items-center gap-3 py-1">
          <div className="flex-1 h-px bg-gradient-to-r from-transparent via-amber-200 to-transparent" />
        </div>

        {/* ── Section 2: Select a Game ── */}
        <div className="pt-3 pb-4" data-testid="section-select-game">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-sm">
              <Gamepad2 className="w-3.5 h-3.5 text-white" />
            </div>
            <h2 className="text-base font-bold text-slate-900">Select a Game</h2>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-violet-500 animate-spin" />
            </div>
          ) : games.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-2xl border border-purple-100">
              <Trophy className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No games available at the moment</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              {games.map((game, idx) => {
                const config = getGameConfig(game);
                return (
                  <button
                    key={game}
                    onClick={() => handleGameSelect(game)}
                    className="group relative bg-white rounded-2xl p-4 shadow-md border border-purple-100 hover:shadow-xl hover:border-purple-200 transition-all duration-300 text-left overflow-hidden"
                    data-testid={`game-card-${game.toLowerCase().replace(/\s+/g, '-')}`}
                    style={{ animationDelay: `${idx * 50}ms` }}
                  >
                    <div className={`absolute inset-0 bg-gradient-to-br ${config.gradient} opacity-0 group-hover:opacity-10 transition-opacity duration-300`} />
                    <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${config.gradient} flex items-center justify-center text-3xl mb-3 shadow-lg ${config.shadow} group-hover:scale-110 transition-transform duration-300`}>
                      {config.isCustomIcon ? <PickleballIcon /> : config.emoji}
                    </div>
                    <h3 className="font-bold text-slate-900 group-hover:text-violet-600 transition-colors text-sm">
                      {game}
                    </h3>
                    <p className="text-xs text-slate-400 mt-0.5">Tap to book &rarr;</p>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Separator */}
        <div className="flex items-center gap-3 py-1">
          <div className="flex-1 h-px bg-gradient-to-r from-transparent via-teal-200 to-transparent" />
        </div>

        {/* ── Section 3: My Bookings ── */}
        <div className="pt-3 pb-2" data-testid="section-my-bookings">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-teal-500 to-emerald-600 flex items-center justify-center shadow-sm">
              <Calendar className="w-3.5 h-3.5 text-white" />
            </div>
            <h2 className="text-base font-bold text-slate-900">My Bookings</h2>
          </div>
          <Link
            to="/my-bookings"
            className="flex items-center gap-3 bg-white rounded-xl p-4 shadow-sm border border-teal-100 hover:border-teal-300 hover:shadow-md transition-all group"
            data-testid="my-bookings-link"
          >
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-100 to-emerald-100 flex items-center justify-center group-hover:from-teal-200 group-hover:to-emerald-200 transition-colors">
              <Calendar className="w-5 h-5 text-teal-600" />
            </div>
            <div className="flex-1">
              <p className="font-semibold text-slate-900 text-sm">View All Bookings</p>
              <p className="text-xs text-slate-400">Upcoming & past games</p>
            </div>
            <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-teal-500 transition-colors" />
          </Link>
        </div>
      </div>

      <CustomerFooter />
      
      <style>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(-4px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in { animation: fade-in 0.3s ease-out; }
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </div>
  );
}
